import { useState, useRef, useEffect } from "react";
import DOMPurify from 'dompurify';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { EmailAutocomplete } from "./EmailAutocomplete";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogHeader,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  X, 
  Paperclip, 
  Send,
  Trash2,
  Minus,
  Maximize2,
  Upload,
  BookOpen,
  FileText,
  Image,
  File,
  Check,
  Sparkles
} from "lucide-react";
import { AIComposeDialog } from "./AIComposeDialog";
import { cn } from "@/lib/utils";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import type { KnowledgeBaseAttachment } from "@/types/team";

interface Organization {
  id: string;
  name: string;
  logo_url?: string;
  color?: string;
}

interface EmailComposeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fromAddress?: string;
  onEmailSent?: () => void;
  senderName?: string;
  organization?: Organization;
  accountId?: string;
  organizationId?: string;
  replyTo?: {
    to: string;
    subject: string;
    body?: string;
    originalHtml?: string;
    mode: 'reply' | 'replyAll' | 'forward';
    cc?: string;
    attachments?: any[];
  } | null;
  userProfile?: {
    first_name?: string;
    last_name?: string;
    phone?: string;
    email?: string;
    address?: string;
    contact_email?: string;
    avatar_url?: string;
  } | null;
}

interface Attachment {
  file: File;
  name: string;
  size: number;
}

interface OrganizationSignature {
  id: string;
  signature_html: string;
  signature_name: string;
  is_default: boolean;
}

export const EmailComposeDialog = ({ 
  open, 
  onOpenChange,
  fromAddress = "",
  onEmailSent,
  senderName,
  organization,
  accountId,
  organizationId,
  replyTo,
  userProfile
}: EmailComposeDialogProps) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [sending, setSending] = useState(false);
  const [signatureHtml, setSignatureHtml] = useState<string>("");
  const [showKnowledgeBaseDialog, setShowKnowledgeBaseDialog] = useState(false);
  const [showAIComposeDialog, setShowAIComposeDialog] = useState(false);
  const [knowledgeBaseEntries, setKnowledgeBaseEntries] = useState<{
    id: string;
    title: string;
    attachments: KnowledgeBaseAttachment[];
  }[]>([]);
  const [loadingKB, setLoadingKB] = useState(false);
  
  // Load organization signature when dialog opens
  useEffect(() => {
    const loadSignature = async () => {
      if (!organizationId || !open) return;
      
      try {
        const { data, error } = await supabase
          .from('organization_signatures')
          .select('*')
          .eq('organization_id', organizationId)
          .eq('is_default', true)
          .single();
        
        if (error && error.code !== 'PGRST116') {
          console.error('Error loading signature:', error);
          return;
        }
        
        if (data) {
          // Replace placeholders with actual user data, removing empty values
          let processedSignature = data.signature_html;
          
          // Helper function to replace or remove placeholders
          const replaceOrRemove = (html: string, pattern: RegExp, value: string | undefined | null): string => {
            if (value && value.trim()) {
              return html.replace(pattern, value);
            }
            // Remove the placeholder and any surrounding whitespace/elements if value is empty
            return html.replace(pattern, '');
          };
          
          if (userProfile) {
            processedSignature = replaceOrRemove(processedSignature, /\{\{vorname\}\}/g, userProfile.first_name);
            processedSignature = replaceOrRemove(processedSignature, /\{\{nachname\}\}/g, userProfile.last_name);
            processedSignature = replaceOrRemove(processedSignature, /\{\{telefon\}\}/g, userProfile.phone);
            processedSignature = replaceOrRemove(processedSignature, /\{\{email\}\}/g, userProfile.contact_email || userProfile.email);
            processedSignature = replaceOrRemove(processedSignature, /\{\{adresse\}\}/g, userProfile.address);
            
            // Handle advisor image with size - {{beraterbild:SIZE}}
            if (userProfile.avatar_url) {
              const avatarUrl = userProfile.avatar_url.startsWith('http') 
                ? userProfile.avatar_url 
                : `https://puwkrtqzszjsjkxowvin.supabase.co/storage/v1/object/public/profile-avatars/${userProfile.avatar_url}`;
              
              processedSignature = processedSignature.replace(
                /\{\{beraterbild:(\d+)\}\}/g, 
                (_, size) => `<img src="${avatarUrl}" alt="Berater" style="width: ${size}px; height: ${size}px; border-radius: 50%; object-fit: cover;" />`
              );
            } else {
              // Remove advisor image placeholder if no avatar
              processedSignature = processedSignature.replace(/\{\{beraterbild:\d+\}\}/g, '');
            }
          } else {
            // Remove all user placeholders if no profile
            processedSignature = processedSignature
              .replace(/\{\{vorname\}\}/g, '')
              .replace(/\{\{nachname\}\}/g, '')
              .replace(/\{\{telefon\}\}/g, '')
              .replace(/\{\{email\}\}/g, '')
              .replace(/\{\{adresse\}\}/g, '')
              .replace(/\{\{beraterbild:\d+\}\}/g, '');
          }
          
          if (organization) {
            processedSignature = replaceOrRemove(processedSignature, /\{\{organisation\}\}/g, organization.name);
            
            // Handle logo with size - {{logo:SIZE}}
            if (organization.logo_url) {
              const logoUrl = organization.logo_url.startsWith('http') 
                ? organization.logo_url 
                : `https://puwkrtqzszjsjkxowvin.supabase.co/storage/v1/object/public/organization-logos/${organization.logo_url}`;
              
              processedSignature = processedSignature.replace(
                /\{\{logo:(\d+)\}\}/g, 
                (_, size) => `<img src="${logoUrl}" alt="${organization.name}" style="width: ${size}px; height: auto;" />`
              );
            } else {
              // Remove logo placeholder if no logo
              processedSignature = processedSignature.replace(/\{\{logo:\d+\}\}/g, '');
            }
          } else {
            processedSignature = processedSignature
              .replace(/\{\{organisation\}\}/g, '')
              .replace(/\{\{logo:\d+\}\}/g, '');
          }
          
          // Clean up only truly empty content (not intentional spacing)
          // Only remove leading/trailing br tags, but preserve internal spacing
          processedSignature = processedSignature
            .replace(/^(\s*<br\s*\/?>\s*)+/g, '')
            .replace(/(\s*<br\s*\/?>\s*)+$/g, '');
          
          setSignatureHtml(processedSignature);
        }
      } catch (error) {
        console.error('Error loading signature:', error);
      }
    };
    
    loadSignature();
  }, [organizationId, open, userProfile, organization]);
  
  // Populate fields when replyTo changes and dialog opens
  useEffect(() => {
    if (replyTo && open) {
      setTo(replyTo.mode === 'forward' ? '' : replyTo.to);
      setSubject(replyTo.subject);
      setBody(replyTo.body || '');
      if (replyTo.cc && replyTo.mode === 'replyAll') {
        setCc(replyTo.cc);
        setShowCc(true);
      }
    }
  }, [replyTo, open]);
  
  // Reset or populate when dialog opens with replyTo
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen && replyTo) {
      setTo(replyTo.mode === 'forward' ? '' : replyTo.to);
      setSubject(replyTo.subject);
      setBody(replyTo.body || '');
      if (replyTo.cc && replyTo.mode === 'replyAll') {
        setCc(replyTo.cc);
        setShowCc(true);
      }
    } else if (!isOpen) {
      // Reset on close
      setTo("");
      setCc("");
      setBcc("");
      setSubject("");
      setBody("");
      setShowCc(false);
      setShowBcc(false);
      setAttachments([]);
    }
    onOpenChange(isOpen);
  };
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const newAttachments = Array.from(files).map(file => ({
        file,
        name: file.name,
        size: file.size
      }));
      setAttachments(prev => [...prev, ...newAttachments]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Load knowledge base entries with attachments
  const loadKnowledgeBaseAttachments = async () => {
    if (!user) return;
    
    setLoadingKB(true);
    try {
      // Get user's team_id from profile
      const { data: profileData } = await supabase
        .from('profiles')
        .select('team_id')
        .eq('user_id', user.id)
        .single();
      
      if (!profileData?.team_id) {
        toast({ title: "Kein Team gefunden", variant: "destructive" });
        return;
      }

      const { data, error } = await supabase
        .from('team_knowledge_base')
        .select('id, title, attachments')
        .eq('team_id', profileData.team_id)
        .not('attachments', 'is', null)
        .order('title');

      if (error) throw error;

      // Filter entries that have attachments
      const entriesWithAttachments = (data || [])
        .map(entry => ({
          id: entry.id,
          title: entry.title,
          attachments: (entry.attachments as unknown as KnowledgeBaseAttachment[]) || []
        }))
        .filter(entry => entry.attachments.length > 0);

      setKnowledgeBaseEntries(entriesWithAttachments);
      setShowKnowledgeBaseDialog(true);
    } catch (error) {
      console.error('Error loading knowledge base:', error);
      toast({ title: "Fehler beim Laden der Wissensbank", variant: "destructive" });
    } finally {
      setLoadingKB(false);
    }
  };

  // Add knowledge base attachment to email
  const addKnowledgeBaseAttachment = async (attachment: KnowledgeBaseAttachment) => {
    try {
      // Fetch the file from storage
      const response = await fetch(attachment.url);
      if (!response.ok) throw new Error('Failed to fetch file');
      
      const blob = await response.blob();
      const fileObj = new globalThis.File([blob], attachment.name, { type: attachment.type });
      
      // Check if already attached
      if (attachments.some(a => a.name === attachment.name)) {
        toast({ title: "Datei bereits angehängt" });
        return;
      }
      
      setAttachments(prev => [...prev, {
        file: fileObj,
        name: attachment.name,
        size: attachment.size
      }]);
      
      toast({ title: `${attachment.name} angehängt` });
    } catch (error) {
      console.error('Error adding attachment:', error);
      toast({ title: "Fehler beim Anhängen der Datei", variant: "destructive" });
    }
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return Image;
    if (type === 'application/pdf') return FileText;
    return File;
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix (e.g., "data:application/pdf;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
    });
  };

  const convertToHtml = (text: string): string => {
    // Escape HTML entities
    const escapeHtml = (str: string) => str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
    
    // Extract only the new message content (before the quoted section separator)
    let newContent = text;
    if (text.includes('--------')) {
      const parts = text.split(/\n\n\n--------/);
      newContent = parts[0] || '';
    }
    
    // Convert to HTML - preserve line breaks
    const html = escapeHtml(newContent.trim())
      .split('\n')
      .map(line => line || '<br>')
      .join('<br>\n');
    
    return `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 14px; line-height: 1.5;">${html}</div>`;
  };

  const handleSend = async () => {
    if (!to.trim()) return;
    
    setSending(true);
    
    try {
      // Convert attachments to base64
      const attachmentData = await Promise.all(
        attachments.map(async (att) => ({
          filename: att.name,
          content: await fileToBase64(att.file),
          contentType: att.file.type || 'application/octet-stream'
        }))
      );

      // Convert plain text to proper HTML with formatting preserved (without quoted content)
      let htmlBody = convertToHtml(body);
      
      // Append signature if available
      if (signatureHtml) {
        htmlBody += `<div style="margin-top: 20px; padding-top: 10px; border-top: 1px solid #e5e5e5;">${signatureHtml}</div>`;
      }

      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          to: to.split(',').map(e => e.trim()).filter(Boolean),
          cc: cc ? cc.split(',').map(e => e.trim()).filter(Boolean) : undefined,
          bcc: bcc ? bcc.split(',').map(e => e.trim()).filter(Boolean) : undefined,
          subject: subject || "(Kein Betreff)",
          html: htmlBody,
          text: body + (signatureHtml ? '\n\n---\n' + signatureHtml.replace(/<[^>]+>/g, '') : ''),
          from: fromAddress,
          fromName: senderName,
          organization: organization ? {
            name: organization.name,
            logoUrl: organization.logo_url,
            color: organization.color
          } : undefined,
          attachments: attachmentData.length > 0 ? attachmentData : undefined
        }
      });

      if (error) throw error;

      // Speichere gesendete E-Mail in der Datenbank
      if (accountId && organizationId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const toAddresses = to.split(',').map(e => e.trim()).filter(Boolean);
          const ccAddresses = cc ? cc.split(',').map(e => e.trim()).filter(Boolean) : null;
          const bccAddresses = bcc ? bcc.split(',').map(e => e.trim()).filter(Boolean) : null;
          
          await supabase.from('emails').insert({
            user_id: user.id,
            account_id: accountId,
            organization_id: organizationId,
            from_address: fromAddress,
            to_addresses: toAddresses,
            cc_addresses: ccAddresses,
            bcc_addresses: bccAddresses,
            subject: subject || "(Kein Betreff)",
            body_html: htmlBody,
            body_text: body,
            status: 'sent',
            sent_at: new Date().toISOString(),
            is_read: true,
            attachments: attachments.length > 0 ? attachments.map(att => ({
              filename: att.name,
              size: att.size,
              contentType: att.file.type || 'application/octet-stream'
            })) : null
          });
        }
      }

      toast({
        title: "E-Mail gesendet",
        description: `Nachricht an ${to} wurde erfolgreich gesendet.`,
      });

      onEmailSent?.();
      handleClose();
    } catch (error: any) {
      console.error("Error sending email:", error);
      toast({
        title: "Fehler beim Senden",
        description: error.message || "Die E-Mail konnte nicht gesendet werden.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setTo("");
    setCc("");
    setBcc("");
    setSubject("");
    setBody("");
    setAttachments([]);
    setShowCc(false);
    setShowBcc(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-xl w-[calc(100%-2rem)] h-[90vh] sm:h-[80vh] flex flex-col p-0 gap-0 rounded-t-xl sm:rounded-xl overflow-hidden fixed bottom-0 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 left-1/2 -translate-x-1/2 [&>button]:hidden">
        <VisuallyHidden>
          <DialogTitle>
            {replyTo?.mode === 'reply' ? 'Antworten' : replyTo?.mode === 'replyAll' ? 'Allen antworten' : replyTo?.mode === 'forward' ? 'Weiterleiten' : 'Neue Nachricht'}
          </DialogTitle>
          <DialogDescription>Verfassen Sie eine E-Mail</DialogDescription>
        </VisuallyHidden>
        
        {/* Header - Apple Mail style */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-muted/80 border-b shrink-0">
          <span className="font-medium text-sm">
            {replyTo?.mode === 'reply' ? 'Antworten' : replyTo?.mode === 'replyAll' ? 'Allen antworten' : replyTo?.mode === 'forward' ? 'Weiterleiten' : 'Neue Nachricht'}
          </span>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-7 w-7 hidden sm:flex">
              <Minus className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 hidden sm:flex">
              <Maximize2 className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Compact form fields */}
        <div className="flex-1 overflow-y-auto">
          {/* From */}
          <div className="flex items-center border-b px-4 py-2 text-sm">
            <span className="text-muted-foreground w-12 shrink-0">Von</span>
            <span className="text-foreground truncate">{fromAddress || "—"}</span>
          </div>

          {/* To */}
          <div className="flex items-center border-b px-4 py-1.5">
            <span className="text-muted-foreground text-sm w-12 shrink-0">An</span>
            <EmailAutocomplete
              value={to}
              onChange={setTo}
              placeholder="Empfänger"
              className="border-0 shadow-none px-0 h-8 text-sm focus-visible:ring-0 bg-transparent"
              organizationId={organizationId}
            />
            <div className="flex items-center gap-1 shrink-0 text-xs">
              {!showCc && (
                <button 
                  onClick={() => setShowCc(true)}
                  className="text-muted-foreground hover:text-foreground px-1"
                >
                  Cc
                </button>
              )}
              {!showBcc && (
                <button 
                  onClick={() => setShowBcc(true)}
                  className="text-muted-foreground hover:text-foreground px-1"
                >
                  Bcc
                </button>
              )}
            </div>
          </div>

          {/* CC */}
          {showCc && (
            <div className="flex items-center border-b px-4 py-1.5">
              <span className="text-muted-foreground text-sm w-12 shrink-0">Cc</span>
              <EmailAutocomplete
                value={cc}
                onChange={setCc}
                placeholder="CC-Empfänger"
                className="border-0 shadow-none px-0 h-8 text-sm focus-visible:ring-0 bg-transparent"
                organizationId={organizationId}
              />
              <button 
                onClick={() => { setShowCc(false); setCc(""); }}
                className="text-muted-foreground hover:text-foreground p-1"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}

          {/* BCC */}
          {showBcc && (
            <div className="flex items-center border-b px-4 py-1.5">
              <span className="text-muted-foreground text-sm w-12 shrink-0">Bcc</span>
              <EmailAutocomplete
                value={bcc}
                onChange={setBcc}
                placeholder="BCC-Empfänger"
                className="border-0 shadow-none px-0 h-8 text-sm focus-visible:ring-0 bg-transparent"
                organizationId={organizationId}
              />
              <button 
                onClick={() => { setShowBcc(false); setBcc(""); }}
                className="text-muted-foreground hover:text-foreground p-1"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}

          {/* Subject */}
          <div className="flex items-center border-b px-4 py-1.5">
            <span className="text-muted-foreground text-sm w-16 shrink-0">Betreff</span>
            <Input 
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Betreff eingeben"
              className="border-0 shadow-none px-0 h-8 text-sm focus-visible:ring-0 bg-transparent ml-2"
            />
          </div>

          {/* Body - New message area with AI compose hint */}
          <div className="relative">
            <Textarea 
              value={replyTo ? body.split('\n\n\n--------')[0] : body}
              onChange={(e) => {
                if (replyTo) {
                  const quotedPart = body.includes('\n\n\n--------') ? '\n\n\n--------' + body.split('\n\n\n--------')[1] : '';
                  setBody(e.target.value + quotedPart);
                } else {
                  setBody(e.target.value);
                }
              }}
              placeholder=""
              className="border-0 shadow-none rounded-none min-h-[120px] sm:min-h-[150px] text-sm resize-none focus-visible:ring-0 px-4 py-3"
            />
            {/* Placeholder with AI compose hint */}
            {!(replyTo ? body.split('\n\n\n--------')[0] : body).trim() && (
              <div className="absolute left-4 top-3 flex items-center gap-1 text-sm pointer-events-none">
                <span className="text-muted-foreground/50">Nachricht verfassen...</span>
                <button
                  type="button"
                  onClick={() => setShowAIComposeDialog(true)}
                  className="pointer-events-auto flex items-center gap-1 text-primary/70 hover:text-primary transition-colors cursor-pointer hover:underline"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  <span>mit KI verfassen</span>
                </button>
              </div>
            )}
          </div>

          {/* Signature Preview */}
          {signatureHtml && (
            <div className="border-t border-border/50 px-4 py-3">
              <div 
                className="text-sm [&_p]:mb-3 [&_p]:mt-0 [&_p:empty]:h-4 [&_br]:block [&_div]:mb-2 [&_img]:inline-block"
                dangerouslySetInnerHTML={{ __html: signatureHtml }}
              />
            </div>
          )}

          {/* Quoted original message - Apple Mail style */}
          {replyTo && replyTo.body && (replyTo.body.includes('-------- Ursprüngliche Nachricht') || replyTo.body.includes('-------- Weitergeleitete Nachricht')) && (
            <div className="border-t border-border/50 bg-muted/30 max-h-[400px] overflow-y-auto">
              <div className="px-4 py-3 border-l-2 border-primary/40 ml-4 mr-4 my-3">
                <div className="text-xs text-muted-foreground mb-2 font-medium">
                  {replyTo.mode === 'forward' ? 'Weitergeleitete Nachricht' : 'Ursprüngliche Nachricht'}
                </div>
                <div className="text-sm text-muted-foreground space-y-1">
                  {(() => {
                    // Split at the separator line
                    const separatorMatch = replyTo.body.match(/-------- (Ursprüngliche|Weitergeleitete) Nachricht --------/);
                    if (!separatorMatch) return null;
                    
                    const separatorIndex = replyTo.body.indexOf(separatorMatch[0]);
                    const afterSeparator = replyTo.body.slice(separatorIndex + separatorMatch[0].length).trim();
                    const lines = afterSeparator.split('\n');
                    const metaLines: string[] = [];
                    let bodyStartIndex = 0;
                    
                    // Extract metadata lines (Von, Datum, Betreff, An)
                    for (let i = 0; i < lines.length; i++) {
                      const line = lines[i].trim();
                      if (line.startsWith('Von:') || line.startsWith('Datum:') || 
                          line.startsWith('Betreff:') || line.startsWith('An:')) {
                        metaLines.push(line);
                        bodyStartIndex = i + 1;
                      } else if (line === '' && metaLines.length > 0) {
                        bodyStartIndex = i + 1;
                        break;
                      } else if (metaLines.length > 0) {
                        bodyStartIndex = i;
                        break;
                      }
                    }
                    
                    const textBody = lines.slice(bodyStartIndex).join('\n').trim();
                    
                    return (
                      <>
                        {metaLines.map((line, idx) => {
                          const colonIndex = line.indexOf(':');
                          const label = line.slice(0, colonIndex);
                          const value = line.slice(colonIndex + 1).trim();
                          return (
                            <div key={idx} className="flex gap-2 text-xs">
                              <span className="font-medium shrink-0 text-muted-foreground">{label}:</span>
                              <span className="break-all text-foreground/70">{value}</span>
                            </div>
                          );
                        })}
                        <div className="mt-3 pt-3 border-t border-border/30">
                          {replyTo.originalHtml ? (
                            <div 
                              className="prose prose-sm dark:prose-invert max-w-none text-foreground/80 [&_img]:max-w-full [&_img]:h-auto"
                              dangerouslySetInnerHTML={{ 
                                __html: DOMPurify.sanitize(replyTo.originalHtml, {
                                  ALLOWED_TAGS: ['p', 'br', 'b', 'i', 'u', 'strong', 'em', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code', 'span', 'div', 'table', 'tr', 'td', 'th', 'thead', 'tbody', 'img', 'hr'],
                                  ALLOWED_ATTR: ['href', 'src', 'alt', 'style', 'class', 'target', 'width', 'height'],
                                  ALLOW_DATA_ATTR: false
                                })
                              }}
                            />
                          ) : textBody ? (
                            <div className="whitespace-pre-wrap font-sans leading-relaxed text-foreground/80 text-sm">
                              {textBody}
                            </div>
                          ) : null}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Attachments - compact chips */}
          {attachments.length > 0 && (
            <div className="px-4 py-2 border-t flex flex-wrap gap-2">
              {attachments.map((attachment, index) => (
                <div 
                  key={index}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted text-xs group"
                >
                  <Paperclip className="h-3 w-3 text-muted-foreground" />
                  <span className="max-w-[120px] truncate">{attachment.name}</span>
                  <span className="text-muted-foreground">({formatFileSize(attachment.size)})</span>
                  <button
                    onClick={() => removeAttachment(index)}
                    className="ml-0.5 opacity-60 hover:opacity-100"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer toolbar */}
        <div className="px-3 py-2 border-t shrink-0 flex items-center justify-between gap-2 bg-background">
          <div className="flex items-center gap-1">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              className="hidden"
              multiple
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                  disabled={sending || loadingKB}
                >
                  <Paperclip className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
                  <Upload className="h-4 w-4 mr-2" />
                  Datei hochladen
                </DropdownMenuItem>
                <DropdownMenuItem onClick={loadKnowledgeBaseAttachments}>
                  <BookOpen className="h-4 w-4 mr-2" />
                  Aus Wissensbank
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-destructive hover:text-destructive"
              onClick={handleClose}
              disabled={sending}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          
          <Button
            size="sm"
            onClick={handleSend}
            disabled={!to.trim() || sending}
            className="px-4"
          >
            <Send className="h-4 w-4 mr-2" />
            {sending ? "Senden..." : "Senden"}
          </Button>
        </div>
      </DialogContent>

      {/* Knowledge Base Attachment Dialog */}
      <Dialog open={showKnowledgeBaseDialog} onOpenChange={setShowKnowledgeBaseDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Anhänge aus Wissensbank
            </DialogTitle>
            <DialogDescription>
              Wähle Dateien aus der Wissensbank zum Anhängen an diese E-Mail
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="flex-1 -mx-6 px-6">
            {knowledgeBaseEntries.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Keine Anhänge in der Wissensbank gefunden</p>
              </div>
            ) : (
              <div className="space-y-4">
                {knowledgeBaseEntries.map(entry => (
                  <div key={entry.id} className="border rounded-lg p-3">
                    <h4 className="font-medium text-sm mb-2">{entry.title}</h4>
                    <div className="space-y-1.5">
                      {entry.attachments.map((attachment, idx) => {
                        const FileIcon = getFileIcon(attachment.type);
                        const isAttached = attachments.some(a => a.name === attachment.name);
                        
                        return (
                          <button
                            key={idx}
                            onClick={() => addKnowledgeBaseAttachment(attachment)}
                            disabled={isAttached}
                            className={cn(
                              "w-full flex items-center gap-2 p-2 rounded-md text-left text-sm transition-colors",
                              isAttached 
                                ? "bg-primary/10 text-primary cursor-default" 
                                : "hover:bg-muted"
                            )}
                          >
                            <FileIcon className="h-4 w-4 shrink-0" />
                            <span className="flex-1 truncate">{attachment.name}</span>
                            <span className="text-xs text-muted-foreground shrink-0">
                              {formatFileSize(attachment.size)}
                            </span>
                            {isAttached && (
                              <Check className="h-4 w-4 text-primary shrink-0" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          
          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setShowKnowledgeBaseDialog(false)}>
              Fertig
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Compose Dialog */}
      <AIComposeDialog
        open={showAIComposeDialog}
        onOpenChange={setShowAIComposeDialog}
        onAccept={(emailText, suggestedSubject) => {
          // Set the generated email as the body
          if (replyTo) {
            const quotedPart = body.includes('\n\n\n--------') ? '\n\n\n--------' + body.split('\n\n\n--------')[1] : '';
            setBody(emailText + quotedPart);
          } else {
            setBody(emailText);
          }
          // Set suggested subject if provided and current subject is empty
          if (suggestedSubject && !subject.trim()) {
            setSubject(suggestedSubject);
          }
        }}
        originalEmail={replyTo ? {
          from: replyTo.to,
          subject: replyTo.subject,
          body: replyTo.body?.split('\n\n\n--------')[1] || replyTo.body,
          attachments: replyTo.attachments,
        } : null}
        userName={userProfile ? `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() : undefined}
        signatureText={signatureHtml ? signatureHtml.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : undefined}
        currentSubject={subject}
        organizationId={organizationId}
      />
    </Dialog>
  );
};
